// Copyright (c) 2026 Solartek LLC. All rights reserved.
import { z } from 'zod';
import { MEMBERSHIP_STATUSES } from '../enums.js';
import { emailSchema, isoDateTimeSchema, paginationQuerySchema, uuidSchema } from '../common.js';
import { PERMISSIONS } from '../permissions.js';
export const memberListQuerySchema = paginationQuerySchema.extend({
    status: z.enum(MEMBERSHIP_STATUSES).optional(),
    roleId: uuidSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const memberDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    userId: uuidSchema,
    email: z.string(),
    fullName: z.string(),
    avatarPath: z.string().nullable(),
    roleId: uuidSchema,
    roleKey: z.string(),
    roleName: z.string(),
    status: z.enum(MEMBERSHIP_STATUSES),
    allBranches: z.boolean(),
    branchIds: z.array(uuidSchema),
    branchNames: z.array(z.string()).optional(),
    employeeId: uuidSchema.nullable(),
    employeeNumber: z.string().nullable().optional(),
    lastLoginAt: isoDateTimeSchema.nullable(),
    joinedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
/** E-mail delivery state of an invitation (invitations.delivery_status). */
export const INVITATION_DELIVERY_STATUSES = ['none', 'queued', 'retrying', 'sent', 'failed'];
export const invitationDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema,
    email: z.string(),
    roleId: uuidSchema,
    roleName: z.string().optional(),
    allBranches: z.boolean(),
    branchIds: z.array(uuidSchema),
    invitedBy: uuidSchema.nullable(),
    invitedByName: z.string().nullable().optional(),
    /** Employee record the membership is linked to when the invitation is accepted (chosen while inviting). */
    employeeId: uuidSchema.nullable().optional(),
    employeeNumber: z.string().nullable().optional(),
    expiresAt: isoDateTimeSchema,
    acceptedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
    /** Present only in the response of the request that created the invitation. */
    token: z.string().optional(),
    /** Present when the invitee already had an account and a membership was created directly. */
    membershipId: uuidSchema.nullable().optional(),
    /** When the worker last e-mailed the invitation (null until it has). */
    deliverySentAt: isoDateTimeSchema.nullable().optional(),
    /** E-mail delivery: none (not queued) | queued | retrying (an attempt failed, the queue retries) | sent | failed (gave up). */
    deliveryStatus: z.enum(INVITATION_DELIVERY_STATUSES).optional(),
    /** Attempts made by the current e-mail job (out of INVITATION_EMAIL_MAX_ATTEMPTS). */
    deliveryAttempts: z.number().int().optional(),
    /** The provider's reason for the last failed attempt. */
    deliveryLastError: z.string().nullable().optional(),
    deliveryLastAttemptAt: isoDateTimeSchema.nullable().optional(),
    /** Approximately when the queue retries (while `retrying`). */
    deliveryNextAttemptAt: isoDateTimeSchema.nullable().optional(),
    /** The mailer that accepted the e-mail; `console` means e-mail delivery is not configured (nothing reached the inbox). */
    deliveryProvider: z.string().nullable().optional(),
});
/** Worker job that e-mails an invitation (it mints the e-mailed token and stores only its hash). */
export const INVITATION_EMAIL_JOB_TYPE = 'SEND_INVITATION_EMAIL';
/** Attempts of one invitation e-mail job (the queue backs off exponentially between them: ~30 s, 1, 2, 4 min). */
export const INVITATION_EMAIL_MAX_ATTEMPTS = 5;
/** One pending e-mail job per invitation: queuing it again while one is pending reuses that job. */
export const invitationEmailDedupeKey = (invitationId) => `invitation-email:${invitationId}`;
/** Result of queuing an invitation e-mail again (an async job, AGENTS.md rule 5). */
export const invitationEmailQueuedSchema = z.object({ jobId: z.string(), status: z.literal('QUEUED'), invitation: invitationDtoSchema });
export const acceptInvitationSchema = z.object({ token: z.string().min(16).max(256) });
export const permissionDtoSchema = z.object({
    key: z.enum(PERMISSIONS),
    category: z.string(),
    description: z.string(),
    sortOrder: z.number().int(),
});
export const roleDtoSchema = z.object({
    id: uuidSchema,
    organizationId: uuidSchema.nullable(),
    key: z.string(),
    name: z.string(),
    description: z.string().nullable(),
    isSystem: z.boolean(),
    permissions: z.array(z.string()),
    memberCount: z.number().int().optional(),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
});
export const updateRoleSchema = z.object({
    name: z.string().trim().min(1).max(80).optional(),
    description: z.string().max(300).nullable().optional(),
    permissions: z.array(z.enum(PERMISSIONS)).min(1).max(PERMISSIONS.length).optional(),
});
export const updateMeSchema = z.object({
    fullName: z.string().trim().min(1).max(160).optional(),
    locale: z.enum(['en', 'ar']).optional(),
});
export const userProfileDtoSchema = z.object({
    id: uuidSchema,
    email: emailSchema.or(z.string()),
    fullName: z.string(),
    avatarPath: z.string().nullable(),
    locale: z.string(),
    mfaEnrolled: z.boolean(),
    status: z.string(),
    lastLoginAt: isoDateTimeSchema.nullable(),
});
// ----- invitations parity (HR portal Prompt 6b, Finance B-67 … B-76) -----------------------------------------------------------
/** What a token stands for, without accepting it. */
export const INVITATION_STATES = ['valid', 'accepted', 'revoked', 'expired'];
/** POST /invitations/validate (public, rate limited): the token's state and who it is for, masked. Nothing is accepted. */
/**
 * POST /invitations/validate. Any token string up to 256 characters reaches the lookup (HR portal Prompt 5 review, P2-7): a
 * malformed token and an unknown one get the same 404 after the same work; only a body without a token string is a 400.
 */
export const validateInvitationSchema = z.object({ token: z.string().min(1).max(256) });
/** The portal (FlowZa Time login) state of one employee record. */
export const PORTAL_ACCESS_STATES = ['none', 'invited', 'active', 'suspended'];
/** Days within which a change of an address field by somebody other than the administrator is flagged. */
export const PORTAL_ADDRESS_RECENT_CHANGE_DAYS = 7;
/**
 * POST /orgs/:orgId/employees/:id/portal-access/invite — the address is REQUIRED (review P0-2: the server never defaults it
 * from employee fields); role defaults to `employee`, scope to the employee's own branch.
 */
export const portalAccessInviteSchema = z.object({
    email: emailSchema,
    roleId: uuidSchema.optional(),
    /** Absent = the employee's own branch only. */
    allBranches: z.boolean().optional(),
    branchIds: z.array(uuidSchema).max(200).optional(),
});
/** Revoke / restore portal access (suspends / reactivates the linked login; the employee link stays). */
export const portalAccessChangeSchema = z.object({ reason: z.string().trim().max(500).optional() });
//# sourceMappingURL=members.js.map